import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Button,
  CircularProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Stack,
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Rating,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";

import {
  useDeleteQuizMutation,
  useGetQuizesQuery,
  useUpdateQuizStatusMutation,
} from "../../Apis/quizApi";
import { setQuiz } from "../../Storage/Redux/quizSlice";
import { QuizModel } from "../../Interfaces/QuizModel";

import { withAdminAuth } from "../../HOC";
import { useGetQuizRatingQuery } from "../../Apis/progressApi";

export default withAdminAuth(function QuizList() {
  const { data, isLoading, refetch } = useGetQuizesQuery(null);

  const [updateQuizStatus] = useUpdateQuizStatusMutation();
  const [deleteQuiz] = useDeleteQuizMutation();
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [localQuizes, setLocalQuizes] = useState<QuizModel[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [snackbar, setSnackbar] = useState<{
      open: boolean;
      message: string;
      severity: "success" | "error" | "info";
    }>({ open: false, message: "", severity: "info" });
  
    const handleCloseSnackbar = () => {
      setSnackbar({ ...snackbar, open: false });
    };
  const QuizRatingCell = ({ quizId }: { quizId: number }) => {
  // Use the hook for this specific row
  const { data, isLoading } = useGetQuizRatingQuery(quizId);

  if (isLoading) return <CircularProgress size={16} />;

  
  const average = data?.averageRate || 0;
  const count = data?.totalRaters || 0;

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Rating value={average} readOnly precision={0.5} size="small" />
      <Typography variant="caption" sx={{ ml: 0.5, color: "text.secondary" }}>
        ({count})
      </Typography>
    </Box>
  );
};

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && data?.result) {
      dispatch(setQuiz(data.result));
      setLocalQuizes(data.result);
    }
  }, [isLoading, data, dispatch]);

  const handleStatusChange = async (id: number, isActive: boolean) => {
    const originalQuizes = [...localQuizes];
    setUpdatingId(id);

    try {
      setLocalQuizes((prev) =>
        prev.map((quiz) =>
          quiz.id === id ? { ...quiz, isActive: isActive } : quiz
        )
      );

      const response = await updateQuizStatus({ id, isActive });
      if (response.data) {
        setSnackbar({
          open: true,
          message: `Quiz ${isActive ? "activated" : "deactivated"} successfully`,
          severity: "success",
        });
        
        refetch();
      } else {
        setSnackbar({
          open: true,
          message: "Failed to update quiz status",
          severity: "error",
        });        
        setLocalQuizes(originalQuizes);
      }
    } catch (error) {
      setSnackbar({
          open: true,
          message: "An error occurred while updating quiz status",
          severity: "error",
      });

      console.error(error);
      setLocalQuizes(originalQuizes);
    } finally {
      setUpdatingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!selectedId) return;

    try {
      await deleteQuiz({ id: selectedId }).unwrap();

      setSnackbar({
        open: true,
        message: "Campaign Deleted Successfully",
        severity: "success",
      });

      refetch();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.data?.errorMessages?.[0] || "Delete failed",
        severity: "error",
      });
    } finally {
      setOpenDialog(false);
      setSelectedId(null);
    }
  };

  return (
    <Card sx={{ boxShadow: 3, borderRadius: 3 }}>
      <CardHeader
        title={
          <Typography
            variant="h5"
            sx={{ color: "#eb3c96", fontWeight: "bold" }}
          >
            Quiz List
          </Typography>
        }
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/Quiz/create")}
            sx={{
              backgroundColor: "rgba(81, 35, 132, 1)",
              "&:hover": { backgroundColor: "rgba(81, 35, 132, 0.9)" },
              borderRadius: 2,
              textTransform: "none",
            }}
          >
            Add New Quiz
          </Button>
        }
      />

      <CardContent>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : data?.result?.length ? (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Rating</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.result.map((quiz: QuizModel, index: number) => (
                  <TableRow key={quiz.id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{quiz.name}</TableCell>
                    <TableCell>
                      <QuizRatingCell quizId={quiz.id} />
                    </TableCell>
                    <TableCell>
                      {new Date(quiz.startDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {new Date(quiz.endDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {updatingId === quiz.id ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CircularProgress size={18} />
                          <Typography variant="body2">Updating...</Typography>
                        </Stack>
                      ) : (
                        <Switch
                          checked={quiz.isActive}
                          onChange={(e) =>
                            handleStatusChange(quiz.id, e.target.checked)
                          }
                          color="primary"
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => navigate(`/Quiz/Details/${quiz.id}`)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        color="secondary"
                        onClick={() => navigate(`/Quiz/Edit/${quiz.id}`)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => {
                          setSelectedId(quiz.id);
                          setOpenDialog(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box textAlign="center" py={4}>
            <Typography
              variant="h6"
              color="error"
              sx={{ fontFamily: "monospace", fontWeight: "bold" }}
            >
              No Data Found
            </Typography>
          </Box>
        )}
      </CardContent>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this campaign?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="inherit">
            Cancel
          </Button>

          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
});
